// Legacy auth service - wraps Supabase auth for backward compatibility
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';

export const authService = {
  /**
   * Sign in with Google OAuth
   */
  async signInWithGoogle(): Promise<User> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    });
    if (error) throw error;
    // Return current user after redirect
    const { data } = await supabase.auth.getUser();
    if (!data.user) throw new Error('Failed to get user');
    return data.user;
  },

  /**
   * Sign in with GitHub OAuth
   */
  async signInWithGithub(): Promise<User> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
      options: {
        redirectTo: window.location.origin,
      },
    });
    if (error) throw error;
    const { data } = await supabase.auth.getUser();
    if (!data.user) throw new Error('Failed to get user');
    return data.user;
  },

  /**
   * Sign out the current user
   */
  async signOut(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  /**
   * Get the current authenticated user
   */
  getCurrentUser(): User | null {
    // This is async in Supabase, return null for sync call
    return null;
  },

  /**
   * Subscribe to auth state changes
   */
  onAuthStateChange(callback: (user: User | null) => void): () => void {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      callback(session?.user ?? null);
    });
    
    // Also get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      callback(session?.user ?? null);
    });
    
    return () => subscription.unsubscribe();
  },

  /**
   * Get the current user's access token
   */
  async getIdToken(): Promise<string | null> {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  }
};

export type { User };
